import { Divider, Stack } from '@mantine/core'
import { readContract } from '@wagmi/core'
import { useEffect, useMemo, useState } from 'react'

import { AIRDROP_CONTRACT_ADDRESS } from '@/constants/blockchain'
import ClaimableAirdrop from '@/contracts/ClaimableAirdrop'
import useSessionStorage from '@/hooks/useSessionStorage'
import { useAuth } from '@/plugins/auth'
import { getWagmiConfig } from '@/plugins/auth/config'
import { processBalance } from '@/utils/utils'

import NoOnChainRewards from './NoOnChainRewards'
import OnChainRewards from './OnChainRewards'
import SyncingAirdrop from './SyncingAirdrop'
import { AIRDROP_API_KEY, AIRDROP_URL, AirdropRecipient, Reward } from './utils'

export const getAirdropRewards = async (address: string): Promise<AirdropRecipient[]> => {
  // Check Rewards status from airdrop API - /airdrop/address/:address/rewards
  const recipientEndpoint = `${AIRDROP_URL}airdrop/address/${address}/rewards`
  const getRecipientRewards = async (): Promise<AirdropRecipient[]> => {
    try {
      const response = await fetch(recipientEndpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Request-Method': 'GET',
          'x-api-key': AIRDROP_API_KEY,
        },
      })
      if (!response.ok) {
        throw new Error(response.statusText)
      }
      const data = (await response.json()) as AirdropRecipient[]
      return data
    } catch (error: any) {
      throw new Error(error.message)
    }
  }
  const addressRewards = await getRecipientRewards()

  if (!addressRewards || addressRewards.length === 0) {
    return []
  }

  // Get active airdrops
  const activeAirdrops = (await readContract(getWagmiConfig(), {
    address: AIRDROP_CONTRACT_ADDRESS as `0x${string}`,
    account: address as `0x${string}`,
    abi: ClaimableAirdrop,
    functionName: 'getActiveAirdrops',
  })) as unknown as BigInt[]

  // Filter rewards based on active airdrops
  const activeAddressRewards = addressRewards.filter(reward =>
    activeAirdrops.some(airdropId => BigInt(reward.AirdropID) === airdropId)
  )

  if (activeAddressRewards.length === 0) {
    return []
  }

  // Check which rewards are still valid from the active address rewards
  const validRewards = (await readContract(getWagmiConfig(), {
    address: AIRDROP_CONTRACT_ADDRESS as `0x${string}`,
    account: address as `0x${string}`,
    abi: ClaimableAirdrop,
    functionName: 'getRewardStatuses',
    args: [
      activeAddressRewards.map(({ AirdropID }) => BigInt(AirdropID)),
      activeAddressRewards.map(({ NodeID }) => NodeID) as `0x${string}`[],
    ],
  })) as unknown as Reward[]

  const addressRewardsFiltered = activeAddressRewards.filter(
    (_, idx) => validRewards[idx].claimed === false && validRewards[idx].revoked === false
  )

  return addressRewardsFiltered
}

interface CheckHubAirdropProps {
  nextStep: () => void
}

const CheckHubAirdrop: React.FC<CheckHubAirdropProps> = ({ nextStep }) => {
  const { address } = useAuth()

  const [rewards, setRewards] = useSessionStorage<AirdropRecipient[]>(
    `addressRewards-${address}`,
    []
  )
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    if (!address) {
      return
    }
    getAirdropRewards(address)
      .then(res => {
        setLoading(false)
        setRewards(res)
      })
      .catch((error: any) => {
        console.error(error)
        setLoading(false)
        setRewards([])
      })
  }, [])

  const totalRewardAmount = useMemo(
    () =>
      processBalance(
        rewards
          .map(reward => reward.RewardAmount)
          .reduce((acc, val) => BigInt(acc) + BigInt(val), BigInt(0))
      ),
    [rewards]
  )

  return (
    <Stack style={{ gap: 40 }}>
      <Divider />
      {!loading && totalRewardAmount.value === BigInt(0) && (
        <NoOnChainRewards nextStep={nextStep} />
      )}
      {loading && <SyncingAirdrop />}
      {!loading && totalRewardAmount.value !== BigInt(0) && (
        <OnChainRewards totalRewardAmount={totalRewardAmount} nextStep={nextStep} />
      )}
    </Stack>
  )
}
export default CheckHubAirdrop
